import styled, { css } from 'styled-components';

const getSemanticSurfaceCard = ({ theme }) =>
  theme.colors.semantic.surface.card;

const getSemanticSurfaceMuted = ({ theme }) =>
  theme.colors.semantic.surface.muted;

const getSemanticTextPrimary = ({ theme }) =>
  theme.colors.semantic.text.primary;

const getSemanticTextSecondary = ({ theme }) =>
  theme.colors.semantic.text.secondary;

const getSemanticBorderDefault = ({ theme }) =>
  theme.colors.semantic.border.default;

/**
 * Obtém a cor primária customizada ou usa o fallback do tema.
 * @param {Object} props - Props do styled-component
 * @returns {string} Cor CSS
 */
const getPrimaryColor = props =>
  props.$primaryColor ||
  props.theme.colors.semantic.action.primary ||
  props.theme.colors.porterSecondary ||
  props.theme.colors.primary500;

/**
 * Obtém a cor de highlight (versão clara da cor primária).
 * Se uma cor customizada for passada, usa uma versão com opacidade.
 * @param {Object} props - Props do styled-component
 * @returns {string} Cor CSS
 */
const getPrimaryHighlight = props => {
  const color = props.$primaryColor;

  if (color && color.startsWith('#')) {
    const sanitizedHex = color.slice(1);

    if (sanitizedHex.length === 3) {
      const normalizedHex = sanitizedHex
        .split('')
        .map(char => `${char}${char}`)
        .join('');

      return `#${normalizedHex}1A`;
    }

    if (sanitizedHex.length === 6) {
      return `#${sanitizedHex}1A`;
    }
  }

  return props.theme.colors.semantic.surface.selection;
};

const sizeModifiers = {
  small: () => css`
    height: 38px;
    padding: 8px 12px;
    font-size: 14px;
  `,
  normal: () => css`
    height: 48px;
    padding: 12px 16px;
    font-size: 15px;
  `,
  large: () => css`
    height: 56px;
    padding: 16px 24px;
    font-size: 16px;
  `,
};

const variantModifiers = {
  default: () => css`
    border-radius: 8px;
  `,
  rounded: () => css`
    border-radius: 24px;
  `,
  square: () => css`
    border-radius: 4px;
  `,
};

/**
 * Input estilizado para máscara com suporte a variantes de tamanho,
 * estilo visual, estados de validação e acessibilidade.
 *
 * @component
 *
 * @prop {('small'|'normal'|'large')} [$size='large'] - Tamanho do input
 * @prop {('default'|'rounded'|'square')} [$variant='default'] - Variante visual
 * @prop {boolean} [$invalid=false] - Estado de erro/validação
 * @prop {string} [$primaryColor] - Cor primária customizada (hex, rgb, etc)
 */
export const StyledInput = styled.input`
  width: 100%;
  border: 1px solid ${getSemanticBorderDefault};
  outline: 0;
  background-color: ${getSemanticSurfaceCard};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  font-family: ${props =>
    props.theme.fontFamilyRoboto || '"Roboto", sans-serif'};
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.5px;
  color: ${getSemanticTextPrimary};
  -webkit-text-fill-color: ${getSemanticTextPrimary};
  caret-color: ${getSemanticTextPrimary};

  ${sizeModifiers.large()}

  ${variantModifiers.default()}

  ${props =>
    props.$size && sizeModifiers[props.$size] && sizeModifiers[props.$size]()}

  ${props =>
    props.$variant &&
    variantModifiers[props.$variant] &&
    variantModifiers[props.$variant]()}

  &::placeholder {
    color: ${getSemanticTextSecondary};
    opacity: 1;
  }

  &:hover {
    border-color: ${getPrimaryColor};
  }

  &:focus {
    background-color: ${getSemanticSurfaceCard};
    color: ${getSemanticTextPrimary};
    -webkit-text-fill-color: ${getSemanticTextPrimary};
    border-color: ${getPrimaryColor};
    box-shadow: 0 0 0 3px ${getPrimaryHighlight};
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${getSemanticSurfaceMuted};
    border-color: ${getSemanticSurfaceMuted};
    color: ${getSemanticTextSecondary};
    -webkit-text-fill-color: ${getSemanticTextSecondary};

    &::placeholder {
      color: ${getSemanticTextSecondary};
    }
  }

  &[readonly] {
    background-color: ${getSemanticSurfaceMuted};
    color: ${getSemanticTextPrimary};
    -webkit-text-fill-color: ${getSemanticTextPrimary};
  }

  ${props =>
    props.$invalid &&
    css`
      border-color: ${props.theme.colors.danger500};

      &:focus {
        border-color: ${props.theme.colors.danger500};
        box-shadow: 0 0 0 3px ${props.theme.colors.danger50};
      }
    `}

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
    -webkit-text-fill-color: ${getSemanticTextPrimary};
    -webkit-box-shadow: 0 0 0 1000px ${getSemanticSurfaceCard} inset;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  @media (hover: none) and (pointer: coarse) {
    font-size: 16px;
  }
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${getSemanticTextPrimary};
  margin-bottom: 4px;
`;

export const ErrorText = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.danger500};
  margin-top: 4px;
`;
